import { phaserEvents, Event } from '../events/EventCenter'
import store from '../stores'
import { setMenuOpen } from '../stores/UserStore'
import inputController, { InputLockReason } from './InputController'
import { requestNativeComputerScreenShareFromSelectedItem } from '../components/LiveKitComputerShareUtils'

/**
 * Centralized keyboard handler for game-wide keyboard shortcuts
 * This ensures consistent behavior across all components
 */
class KeyboardHandler {
  private isInitialized = false
  private eventListeners: Array<{ type: string; handler: EventListener }> = []

  /**
   * Initialize the keyboard handler
   */
  initialize(): void {
    if (this.isInitialized) return

    // Add global keyboard event listeners
    this.addGlobalKeyListeners()
    
    this.isInitialized = true
    console.log('[KeyboardHandler] Initialized')
  }

  /**
   * Add global keyboard event listeners
   */
  private addGlobalKeyListeners(): void {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle keys if input is locked for chat focus
      if (inputController.isLockedFor(InputLockReason.CHAT_FOCUSED)) {
        return
      }

      // Don't handle keys if user is typing in an input field
      const target = event.target as HTMLElement
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.closest('input') ||
                          target.closest('textarea')

      if (isInputField) {
        return
      }

      switch (event.key) {
        case 'r':
        case 'R':
          requestNativeComputerScreenShareFromSelectedItem()
          break
        case 'Tab':
          this.handleTabKey(event)
          break
        case 'Escape':
          this.handleEscapeKey(event)
          break
      }
    }

    // Add event listener with capture to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, { capture: true })
    this.eventListeners.push({ type: 'keydown', handler: handleKeyDown })
  }

  /**
   * Handle TAB key press
   */
  private handleTabKey(event: KeyboardEvent): void {
    // Prevent default TAB behavior (focus navigation)
    event.preventDefault()
    event.stopPropagation()

    // Toggle menu — ProfileMenu owns MENU lock/unlock via menuOpen effect
    const currentState = store.getState().user.menuOpen
    store.dispatch(setMenuOpen(!currentState))

    console.log(`[KeyboardHandler] TAB pressed - Menu ${!currentState ? 'opened' : 'closed'}`)
  }

  /**
   * Handle ESC key press
   */
  private handleEscapeKey(event: KeyboardEvent): void {
    // Prevent default ESC behavior
    event.preventDefault()
    event.stopPropagation()

    // Close menu if open
    const currentState = store.getState().user.menuOpen
    if (currentState) {
      store.dispatch(setMenuOpen(false))
      console.log('[KeyboardHandler] ESC pressed - Menu closed')
    }

    // Emit event for other components to handle ESC
    phaserEvents.emit(Event.ESCAPE_PRESSED)
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    this.eventListeners.forEach(({ type, handler }) => {
      window.removeEventListener(type, handler, { capture: true })
    })
    this.eventListeners = []
    this.isInitialized = false
    console.log('[KeyboardHandler] Destroyed')
  }
}

// Create singleton instance
const keyboardHandler = new KeyboardHandler()
export default keyboardHandler
